import * as Yup from 'yup';
import convert from 'convert-units';
import { isEqual } from 'date-fns';

import {
	calculateNextValidDate,
	isPreferredAssessmentTimeAllowed,
} from '../utils/assessmentDateTimeCalculator';
import type { TestProps } from './test.types';

const status = {
	readyForPayment: 'READY_FOR_PAYMENT',
	readyForSchedule: 'READY_FOR_SCHEDULE',
	readyForAssessment: 'READY_FOR_ASSESSMENT',
	readyForApproval: 'READY_FOR_APPROVAL',
	completed: 'COMPLETED',
	cancelled: 'CANCELLED',
};

const paymentStatus = {
	fullyPaid: 'FULLY_PAID',
	unpaid: 'UNPAID',
	depositPaid: 'DEPOSIT_PAID',
	paymentError: 'PAYMENT_ERROR',
};

// Attendance at the booked assessment. Short codes mirror pes-api's ATTENDANCE_STATUS enum.
const attendanceStatus = {
	pending: 'PEN',
	attendedOnTime: 'AOT',
	didNotAttend: 'DNA',
	rescheduled: 'RSC',
};

export type Status = (typeof status)[keyof typeof status];
export type PaymentStatus = (typeof paymentStatus)[keyof typeof paymentStatus];
export type AttendanceStatus = (typeof attendanceStatus)[keyof typeof attendanceStatus];

export const attendanceStatusLabel: Record<AttendanceStatus, string> = {
	PEN: 'Pending',
	AOT: 'Attended on time',
	DNA: 'Did not attend',
	RSC: 'Rescheduled',
};

export interface RequestProps {
	id: string;
	assessmentDates: Date[];
	prefAssessmentDates: (Date | null)[];
	status: Status;
	supplierId: string;
	tests: TestProps[];
	candidate: CandidateProps;
	tncAccepted: boolean;
	isArchived?: boolean;
	createdAt?: Date;
	extraInfo?: string;
	paymentStatus?: PaymentStatus;
	attendanceStatus?: AttendanceStatus;
	adminNote?: string;
	accountNote?: string;
}

export interface CandidateProps {
	firstname: string;
	lastname: string;
	employer: string | null;
	jobRole: string | null;
	phone: string;
	email: string;
	addrState: string;
	addrPostcode: string;
	questionnaireId?: number | null;
}

export interface RequestAssetProps {
	Key: string;
	uri?: string;
}

const candidateSchema = Yup.object({
	firstname: Yup.string().default('').required('Required'),
	lastname: Yup.string().default('').required('Required'),
	employer: Yup.string().nullable().default(''),
	jobRole: Yup.string().nullable().default(''),
	phone: Yup.string()
		.matches(/^\d{10}$/, 'Must be only digits and 10 characters long')
		.default('')
		.required('Required'),
	email: Yup.string().email('Invalid email').default('').required('Required'),
	addrState: Yup.string().default('').trim().required('Required'),
	addrPostcode: Yup.string().default('').trim().required('Required'),
	questionnaireId: Yup.number().nullable().default(null),
});

const schema = Yup.object().shape({
	id: Yup.string().default(''),
	assessmentDates: Yup.array().of(Yup.date().nullable()).nullable(),
	// for users to choose a preferred date when creating a request
	prefAssessmentDates: Yup.array()
		.of(Yup.date().nullable())
		.max(2)
		.required()
		.default(() => [calculateNextValidDate(new Date()), null])
		.test(
			'allowed-time',
			'Preferred assessment date must be a business day between 8:30am and 4:30pm (30-minute steps).',
			(dates) => {
				if (!Array.isArray(dates)) return false;

				const filtered = dates.filter((d): d is Date => d instanceof Date);

				return filtered.every((date) => isPreferredAssessmentTimeAllowed(date));
			},
		)
		.test('no-duplicates', 'Preferred assessment dates must be unique (including time)', (dates) => {
			if (!Array.isArray(dates)) return false;

			const filtered = dates.filter((d): d is Date => d instanceof Date);

			const uniqueDates = filtered.reduce((prev: Date[], next) => {
				const alreadyExists = prev.some((d) => {
					return isEqual(d, next);
				});
				if (!alreadyExists) prev.push(next);
				return prev;
			}, []);

			// if false there is duplicated dates
			return uniqueDates.length === filtered.length;
		}),
	extraInfo: Yup.string().default(null).nullable(),
	status: Yup.mixed<Status>().oneOf(Object.values(status)).default('Unknown'),
	supplierId: Yup.string().default('').required('Please select a test centre.'),
	tests: Yup.array().default([]).min(1).required(),
	candidate: candidateSchema,
	tncAccepted: Yup.boolean()
		.oneOf([true], 'You must accept the terms and conditions.')
		.default(false)
		.required('You must accept the terms and conditions.'),
});

const candidatePersonalInfoSchema = Yup.object({
	candidate: candidateSchema.pick([
		'firstname',
		'lastname',
		'phone',
		'email',
		'employer',
		'jobRole',
		'addrPostcode',
		'addrState',
	]),
});
const testsSchema = schema.pick(['tests']);
const testingCentreSchema = schema.pick(['supplierId']);
const preferredAssessmentDateSchema = schema.pick(['prefAssessmentDates']);
const reviewSchema = schema.pick(['tncAccepted']);
const editSchema = schema.omit(['tncAccepted', 'prefAssessmentDates']);

const editableStatus = [status.readyForPayment, status.readyForSchedule, status.readyForAssessment];

const preferredAssessmentDateLabels = ['Preferred Assessment Date', 'Second Preferred Assessment Date'];

const normalizeAddress = (addrState: CandidateProps['addrState'], addrPostcode: CandidateProps['addrPostcode']) => {
	return `${addrState} ${addrPostcode}`;
};

const isAddressValid = (addrState: CandidateProps['addrState'], addrPostcode: CandidateProps['addrPostcode']) => {
	try {
		schema.validateSyncAt('candidate.addrState', {
			candidate: {
				addrState,
			},
		});

		schema.validateSyncAt('candidate.addrPostcode', {
			candidate: {
				addrPostcode,
			},
		});

		return true;
	} catch (err) {
		return false;
	}
};

const ifMarkTestChecked =
	(requestedTests: TestProps[] = []) =>
	(test: Partial<TestProps> = {}): boolean => {
		return requestedTests.findIndex((t) => t.id?.toString() === test.id?.toString()) > -1;
	};

const formatSupplierDistance = (distanceInMeters: number) => {
	return `${convert(distanceInMeters).from('m').to('km').toFixed(2)}km`;
};

const getReportName = (s3ObjectKey: string) => {
	return s3ObjectKey.split('/').at(-1);
};

// remove custom cast and use another way - see how setting supplier role in user edit is handled
const cast = (params: RequestProps) => {
	const req = schema.cast(params) as RequestProps;

	return {
		...req,
	};
};

const disableAssessmentSchedulling = (currentStatus: Status): boolean => {
	return ([status.readyForApproval, status.cancelled, status.completed] as Status[]).includes(currentStatus);
};

const isAssessmentScheduled = (currentStatus: Status): boolean => {
	return status.readyForAssessment === currentStatus;
};

const isEditable = (currentStatus: Status): boolean => {
	return (editableStatus as Status[]).includes(currentStatus);
};

const isStatusEditingAllowed = (currentStatus: Status, isAdmin: boolean = false): boolean => {
	if (isAdmin && currentStatus === status.completed) {
		return true;
	}
	return ([status.completed, status.cancelled] as Status[]).includes(currentStatus) === false;
};

const getAllowedEditableStatusAdmin = (): Status[] => {
	return [
		status.readyForPayment,
		status.readyForSchedule,
		status.readyForAssessment,
		status.readyForApproval,
		status.completed,
	];
};

const getAllowedEditableStatusSupplier = (): Status[] => {
	return [status.readyForApproval];
};

const getAllowedEditableStatusContractor = (): Status[] => {
	return [status.readyForApproval];
};

const getAllowedEditableStatusStaff = (): Status[] => {
	return [status.readyForApproval, status.completed];
};

const disableTestsEditing = (currentStatus: Status): boolean => {
	return (
		[
			status.readyForSchedule,
			status.readyForAssessment,
			status.readyForApproval,
			status.cancelled,
			status.completed,
		] as Status[]
	).includes(currentStatus);
};

const isPreferredAssessmentTimeEditingAllowed = (currentStatus: Status): boolean => {
	return ([status.readyForSchedule, status.readyForPayment] as Status[]).includes(currentStatus) === true;
};

export const Request = {
	schema,
	editSchema,
	testsSchema,
	candidatePersonalInfoSchema,
	preferredAssessmentDateSchema,
	reviewSchema,
	testingCentreSchema,

	preferredAssessmentDateLabels,
	status,
	paymentStatus,
	attendanceStatus,
	attendanceStatusLabel,

	// utility functions
	cast,
	disableAssessmentSchedulling,
	disableTestsEditing,
	formatSupplierDistance,
	getAllowedEditableStatusAdmin,
	getAllowedEditableStatusStaff,
	getAllowedEditableStatusSupplier,
	getAllowedEditableStatusContractor,
	getReportName,
	ifMarkTestChecked,
	isAddressValid,
	isAssessmentScheduled,
	isPreferredAssessmentTimeEditingAllowed,
	isEditable,
	isStatusEditingAllowed,
	normalizeAddress,
};
